import { Component, OnInit } from '@angular/core';
import { ApiService } from '../services/api.service';
import { Complaint } from '../model/complaint';

interface ComplaintWithMetrics extends Complaint {
  hoursOpen?: number;
  hoursToResolve?: number;
}

@Component({
  selector: 'app-complaints-dashboard-fui',
  templateUrl: './complaints-dashboard-fui.component.html',
  styleUrls: ['./complaints-dashboard-fui.component.scss']
})
export class ComplaintsDashboardFuiComponent implements OnInit {
  complaints: ComplaintWithMetrics[] = [];
  openComplaints: ComplaintWithMetrics[] = [];
  resolvedComplaints: ComplaintWithMetrics[] = [];
  totalOpen = 0;
  avgHoursOpen = 0;
  totalResolvedLast7Days = 0;
  loading = false;
  searchText = '';

  // Pagination properties
  currentPage = 1;
  pageSize = 50;
  totalComplaints = 0;

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.loadComplaints();
  }

  private loadComplaints(): void {
    console.log('🔄 DEBUG: Starting to load complaints...');
    this.loading = true;
    this.apiService.getComplaints(0, 50000, '').subscribe(data => {
      const complaintsData = data?.content || [];
      console.log('📊 DEBUG: Received complaints data count:', complaintsData?.length || 0);
      const now = new Date();
      
      // Process data in chunks to avoid blocking UI
      this.processComplaintsInChunks(complaintsData, now);
    }, error => {
      console.error('❌ ERROR: Failed to load complaints:', error);
      this.loading = false;
    });
  }

  private processComplaintsInChunks(data: Complaint[], now: Date, chunkSize = 500): void {
    const totalChunks = Math.ceil(data.length / chunkSize);
    let currentChunk = 0;
    this.complaints = [];

    const processChunk = () => {
      if (currentChunk >= totalChunks) {
        console.log('✅ DEBUG: Processed all complaints:', this.complaints.length);
        this.applyFilters();
        this.loading = false;
        return;
      }

      const start = currentChunk * chunkSize;
      const end = Math.min(start + chunkSize, data.length);
      const chunk = data.slice(start, end);

      // Process this chunk
      const processedChunk = chunk.map((c: Complaint) => {
        // Fast date parsing - ensure consistent ISO format with T
        const dateLogged = c.dateLogged && c.dateLogged !== 'null' && c.dateLogged !== '' 
          ? new Date(c.dateLogged.includes('T') ? c.dateLogged : c.dateLogged.replace(' ', 'T')) 
          : null;
        const resolvedDate = c.resolvedDate && c.resolvedDate !== 'null' && c.resolvedDate !== ''
          ? new Date(c.resolvedDate.includes('T') ? c.resolvedDate : c.resolvedDate.replace(' ', 'T')) 
          : null;

        let hoursOpen: number | undefined;
        let hoursToResolve: number | undefined;

        if (dateLogged && !isNaN(dateLogged.getTime())) {
          if (!resolvedDate || isNaN(resolvedDate.getTime())) {
            hoursOpen = (now.getTime() - dateLogged.getTime()) / 36e5;
          } else {
            hoursToResolve = (resolvedDate.getTime() - dateLogged.getTime()) / 36e5;
          }
        }

        return {
          ...c,
          hoursOpen,
          hoursToResolve
        };
      });

      this.complaints.push(...processedChunk);
      currentChunk++;

      // Process next chunk with setTimeout to allow UI updates
      setTimeout(processChunk, 10);
    };

    processChunk();
  }

  applyFilters(): void {
    const text = (this.searchText || '').toLowerCase().trim();

    const filtered = text
      ? this.complaints.filter(c =>
          (c.complaintId && c.complaintId.toString().includes(text)) ||
          (c.customerName && c.customerName.toLowerCase().includes(text)) ||
          (c.selectedArea && c.selectedArea.toLowerCase().includes(text)) ||
          (c.engineerName && c.engineerName.toLowerCase().includes(text)) ||
          (c.loggedUser && c.loggedUser.toString().includes(text))
        )
      : this.complaints;

    this.openComplaints = filtered
      .filter(c => c.resolvedStatus !== 'Resolved')
      .sort((a, b) => (b.hoursOpen || 0) - (a.hoursOpen || 0));

    this.resolvedComplaints = filtered
      .filter(c => c.resolvedStatus === 'Resolved')
      .sort((a, b) => (b.resolvedDate || '').localeCompare(a.resolvedDate || ''));

    this.totalOpen = this.openComplaints.length;

    const openWithHours = this.openComplaints.filter(c => typeof c.hoursOpen === 'number');
    this.avgHoursOpen = openWithHours.length
      ? openWithHours.reduce((sum, c) => sum + (c.hoursOpen || 0), 0) / openWithHours.length
      : 0;

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    this.totalResolvedLast7Days = this.resolvedComplaints.filter(c => {
      if (!c.resolvedDate) { return false; }
      const d = new Date(c.resolvedDate.replace(' ', 'T'));
      return d >= sevenDaysAgo;
    }).length;
  }

  formatHours(value?: number): string {
    if (value === undefined || value === null) {
      return '-';
    }
    return value.toFixed(1);
  }
}
